import type { FormSchema } from '@kfolks/types'
import type { ValidationError, ValidationResult } from './form-schema-validator.js'

/**
 * Shared `summaryTemplate` validator. Consumed by:
 *   - Backend `RequestTypeService.create` / `.update` on save (paired with
 *     `validateFormSchema`).
 *   - Frontend admin form-schema editor for inline feedback.
 *
 * The summary template is a Mustache-style one-liner that renders against
 * the request's `formData` (with `__labels` snapshot expansion) to produce a
 * human-friendly row summary on `/requests-center/my`, dashboard widgets,
 * and the digest email. `null`/empty → use the auto-generator fallback.
 *
 * What this validator enforces:
 *   - Every `{{token}}` references a real `formSchema.fields[].id`.
 *   - Tokens use the documented identifier shape (`[a-zA-Z0-9_]+`).
 *   - Total length stays under the column cap (matches the Zod check).
 *
 * What it does NOT do:
 *   - Type-check what the field resolves to (rendering tolerates strings,
 *     numbers, arrays, missing values uniformly).
 *   - Attempt rendering with sample data; rendering is the runtime
 *     concern (`RequestSummaryRenderer`).
 */

export interface SummaryTemplateValidationContext {
	formSchema: Pick<FormSchema, 'fields'> | { fields?: ReadonlyArray<{ id?: unknown }> }
}

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g
/**
 * Mustache-style "section" block, mirrored from the renderer:
 * `{{#field}}…{{/field}}` keeps inner content only when `field` resolves
 * to a non-empty value at render time. Used to wrap optional fields so
 * surrounding literal text disappears when the field is empty.
 */
const SECTION_PATTERN = /\{\{#\s*([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/\s*([a-zA-Z0-9_]+)\s*\}\}/g
/**
 * Catches malformed bits like `{{ field-id }}` (kebab-case is documented as
 * not allowed because the renderer's path is `formData[token]` and our
 * existing field ids are snake_case / camelCase). We surface them as
 * errors so authors don't get a silently-empty render at runtime.
 */
const SUSPICIOUS_TOKEN_PATTERN = /\{\{[^}]*\}\}/g

function collectFieldIds(formSchema: SummaryTemplateValidationContext['formSchema']): Set<string> {
	const fieldIds = new Set<string>()
	const rawFields = formSchema.fields ?? []
	for (const f of rawFields) {
		if (f && typeof (f as { id?: unknown }).id === 'string') {
			fieldIds.add((f as { id: string }).id)
		}
	}
	return fieldIds
}

function validateSectionBlocks(
	template: string,
	fieldIds: Set<string>,
	wellFormedLiterals: Set<string>,
	errors: ValidationError[],
): void {
	// Section blocks. Open + close tags must reference the same field id,
	// and that id must exist in the form schema. Mismatched pairs
	// (`{{#a}}…{{/b}}`) are author errors, not silent renders.
	for (const match of template.matchAll(SECTION_PATTERN)) {
		const opener = match[1]
		const closer = match[3]
		wellFormedLiterals.add(`{{#${opener}}}`)
		wellFormedLiterals.add(`{{/${closer}}}`)
		if (opener !== closer) {
			errors.push({
				path: 'summaryTemplate',
				message: `summaryTemplate has mismatched section tags: opens "{{#${opener}}}" but closes "{{/${closer}}}"`,
				severity: 'error',
			})
		}
		if (!fieldIds.has(opener)) {
			errors.push({
				path: 'summaryTemplate',
				message: `summaryTemplate section references unknown field id "${opener}"`,
				severity: 'error',
			})
		}
	}
}

function validateBalancedSectionMarkers(template: string, errors: ValidationError[]): void {
	// Detect orphan section markers — an opener without a matching close, or
	// a close without an open. The greedy pair matcher above would have
	// consumed a balanced run; anything left over is the author's bug.
	const openMarkers = template.match(/\{\{#\s*[a-zA-Z0-9_]+\s*\}\}/g) ?? []
	const closeMarkers = template.match(/\{\{\/\s*[a-zA-Z0-9_]+\s*\}\}/g) ?? []
	if (openMarkers.length !== closeMarkers.length) {
		errors.push({
			path: 'summaryTemplate',
			message: `summaryTemplate has unbalanced section markers (${openMarkers.length} open, ${closeMarkers.length} close)`,
			severity: 'error',
		})
	}
}

function validateTokens(
	template: string,
	fieldIds: Set<string>,
	wellFormedLiterals: Set<string>,
	errors: ValidationError[],
): void {
	// Every well-formed `{{token}}` must reference a real field.
	for (const match of template.matchAll(TOKEN_PATTERN)) {
		const token = match[1]
		wellFormedLiterals.add(`{{${token}}}`)
		if (!fieldIds.has(token)) {
			errors.push({
				path: 'summaryTemplate',
				message: `summaryTemplate references unknown field id "${token}"`,
				severity: 'error',
			})
		}
	}
}

function validateMalformedTokens(template: string, wellFormedLiterals: Set<string>, errors: ValidationError[]): void {
	// Catch malformed bits (`{{ field-id }}`, `{{}}`, `{{a b}}`) that don't
	// match the strict patterns but look like the author meant a token or
	// section. Better to fail loud at save time than render an empty string.
	for (const match of template.matchAll(SUSPICIOUS_TOKEN_PATTERN)) {
		const raw = match[0]
		// Skip ones a strict pattern already matched — those are well-formed.
		const trimmed = raw.replace(/[\s{}]/g, '')
		const reconstructed = `{{${trimmed}}}`
		if (wellFormedLiterals.has(reconstructed)) continue
		errors.push({
			path: 'summaryTemplate',
			message: `summaryTemplate contains malformed token "${raw}" (expected {{field_id}} or {{#field_id}}…{{/field_id}} with [a-zA-Z0-9_])`,
			severity: 'error',
		})
	}
}

export function validateSummaryTemplate(
	template: string | null | undefined,
	context: SummaryTemplateValidationContext,
): ValidationResult {
	const errors: ValidationError[] = []

	// `null`/empty/whitespace-only is valid — caller falls back to the
	// auto-generator. We don't flag a missing template; absence is allowed.
	if (template === null || template === undefined || template.trim().length === 0) {
		return { valid: true, errors }
	}

	if (template.length > 500) {
		errors.push({
			path: 'summaryTemplate',
			message: 'summaryTemplate exceeds 500 characters',
			severity: 'error',
		})
	}

	const fieldIds = collectFieldIds(context.formSchema)

	// Track every literal `{{…}}` chunk the strict patterns matched so the
	// suspicious-token sweep at the bottom doesn't double-flag well-formed
	// section/token markers.
	const wellFormedLiterals = new Set<string>()

	validateSectionBlocks(template, fieldIds, wellFormedLiterals, errors)
	validateBalancedSectionMarkers(template, errors)
	validateTokens(template, fieldIds, wellFormedLiterals, errors)
	validateMalformedTokens(template, wellFormedLiterals, errors)

	return { valid: errors.length === 0, errors }
}
